import type { MyAnnouncementFieldsFragment } from '@paintscout/api'
import { isPageViewTrigger } from '@paintscout/util/engagement'
import { safeHttpUrl } from 'shared/components/engagement/announcementLinks'

export type Announcement = MyAnnouncementFieldsFragment

/** sessionStorage key: set once an announcement has opened by itself in this tab (#25346). */
export const SESSION_SHOWN_KEY = 'ps-announcement-shown'

/** Newest first by `publishedAt`, the order the history list shows. Does not mutate the input. */
export function sortNewestFirst(announcements: readonly Announcement[]): Announcement[] {
  return [...announcements].sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''))
}

/** An announcement the user has not opened yet: no `viewedAt` and no `dismissedAt`. */
export function isUnread(announcement: Announcement): boolean {
  return !announcement.viewedAt && !announcement.dismissedAt
}

/** The header badge count. */
export function unreadCount(announcements: readonly Announcement[]): number {
  return announcements.filter(isUnread).length
}

/**
 * The startup announcement that opens by itself now, or null. At most one opens per session: once
 * one has opened (`alreadyShownThisSession`), nothing else opens until the next session, and the
 * rest wait in the history list. The newest unread one wins. A page-view announcement (#25610)
 * never opens here: it waits for its page (`isPageViewCandidate`).
 */
export function pickAutoShow(
  announcements: readonly Announcement[],
  alreadyShownThisSession: boolean
): Announcement | null {
  if (alreadyShownThisSession) return null
  return sortNewestFirst(announcements).find((a) => isUnread(a) && !isPageViewTrigger(a)) ?? null
}

/**
 * The "once" rule for a page-view announcement (#25610): unread (the stored `viewedAt` and
 * `dismissedAt`), and not already opened in this tab, so a failed write cannot reopen it on every
 * visit. Page-view announcements are not limited to one per session.
 */
export function isPageViewCandidate(announcement: Announcement, shownInTab: ReadonlySet<string>): boolean {
  return isUnread(announcement) && !shownInTab.has(announcement.id)
}

/** Whether an announcement already opened by itself in this tab. False when storage throws. */
export function readSessionShown(storage: Pick<Storage, 'getItem'> | undefined = safeSessionStorage()): boolean {
  try {
    return !!storage?.getItem(SESSION_SHOWN_KEY)
  } catch {
    return false
  }
}

/** Records that an announcement opened by itself in this tab. Never throws. */
export function writeSessionShown(
  id: string,
  storage: Pick<Storage, 'setItem'> | undefined = safeSessionStorage()
): void {
  try {
    storage?.setItem(SESSION_SHOWN_KEY, id)
  } catch {
    // Private mode or blocked storage: the in-memory guard in AnnouncementCenter still holds.
  }
}

function safeSessionStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}

export type AnnouncementStateName = 'viewed' | 'dismissed' | 'ctaClicked'

const STATE_FIELDS = {
  viewed: 'viewedAt',
  dismissed: 'dismissedAt',
  ctaClicked: 'ctaClickedAt'
} as const

/** Whether the user's state already has this timestamp. The API sets each one once. */
export function hasState(announcement: Announcement, state: AnnouncementStateName): boolean {
  return !!announcement[STATE_FIELDS[state]]
}

/**
 * The announcement as the API returns it after `markAnnouncement(state)`, for `optimisticResponse`:
 * the timestamp is set to `nowIso` only when it was unset, as the API does.
 */
export function withState(announcement: Announcement, state: AnnouncementStateName, nowIso: string): Announcement {
  const field = STATE_FIELDS[state]
  return announcement[field] ? announcement : { ...announcement, [field]: nowIso }
}

/** The CTA URL when it is http(s), else null. The API validates this on save; check again here. */
export function safeCtaUrl(announcement: Announcement): string | null {
  return safeHttpUrl(announcement.cta?.url)
}

/** Opens the announcement's CTA in a new tab, with no access back to this window. */
export function openCta(announcement: Announcement): void {
  const url = safeCtaUrl(announcement)
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}
